<script setup lang="ts">
const emit = defineEmits(["offerConfirmed", "back", "error", "close"]);
const { t } = useI18n();
const { getEstimatedGasFees } = useTransactionsController();
const userStore = useUserStore();
const GFALExStore = useGFALExStore();

const props = defineProps({
  offerAmount: {
    type: String,
    required: true,
  },
  kycUri: {
    type: String,
    required: false,
    default: "",
  },
});
const isLoading = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref<string>();
const gasFees = ref<number>(0);
const gasFeesCovered = ref<boolean>(false);
const gasFeesReason = ref<string | null>(null);

const { presignedWeb2OffersEnabled } = useRuntimeConfig().public;
const config = useRuntimeConfig();

let translationKey = presignedWeb2OffersEnabled
  ? "main.modalOfferBegin.amountPresigned"
  : "main.modalOfferBegin.amountHeld";

onMounted(async () => {
  isLoading.value = true;
  await getGasFees();
  //console.log("presignedWeb2OffersEnabled:", presignedWeb2OffersEnabled);
  isLoading.value = false;
  if (!gasFeesCovered.value && userStore.balance < Number(gasFees.value)) {
    errorMessage.value = t("errorMessages.transferInsufficientFundsForGas");
  }
});

const getGasFees = async () => {
  try {
    const gasResponse = await getEstimatedGasFees(TransactionType.OFFER);
    if (!gasResponse) throw new Error("No gas fees response");
    gasFees.value = gasResponse?.amount ?? 0;
    gasFeesCovered.value = gasResponse?.strategy === "COVERED";
    gasFeesReason.value = gasResponse?.reason;
  } catch (error) {
    emit("error");
  }
};

async function transfer() {
  isSubmitting.value = true;
  emit("offerConfirmed");
}

const subTotal = computed(() => {
  return roundNum(Number(props.offerAmount), 2);
});

const subTotalUSD = computed(() => {
  return roundNum(Number(props.offerAmount) * GFALExStore.GFALExRate, 2);
});

const total = computed(() => {
  if (gasFeesCovered.value) {
    return roundNum(Number(props.offerAmount), 2);
  } else {
    return roundNum(Number(props.offerAmount) + gasFees.value, 2);
  }
});

const totalUSD = computed(() => {
  if (gasFeesCovered.value) {
    return roundNum(Number(props.offerAmount) * GFALExStore.GFALExRate, 2);
  } else {
    return roundNum(
      (Number(props.offerAmount) + gasFees.value) * GFALExStore.GFALExRate,
      2
    );
  }
});
</script>
<template>
  <div>
    <p class="text-secondary-200 text-xl font-bold mt-2">
      {{ $t("main.modalOfferBegin.summary") }}
    </p>
    <div
      class="pt-3 pb-5 flex flex-col gap-1 transition-all duration-500 border-b border-b-neutral-500">
      <div class="flex justify-between text-neutral-300">
        <p class="font-bold">
          {{ $t("main.modalOfferBegin.offerAmount") }}
        </p>
        <div class="flex flex-col">
          <p class="font-bold text-right notranslate">{{ subTotal }} GFAL</p>
          <p class="text-secondary-200 text-right text-xs">
            ~
            {{ subTotalUSD }}
            USD
          </p>
        </div>
      </div>
      <CommonGasFees
        :gas-fees="gasFees"
        :covered="gasFeesCovered"
        :reason="gasFeesReason"
        :is-loading="isLoading" />
    </div>
    <div class="py-2 flex justify-between">
      <p class="text-neutral-200 font-bold text-xl">
        {{ $t("main.modalOfferBegin.offerTotal") }}
      </p>
      <div>
        <p class="text-xl text-neutral-200 font-bold notranslate">
          {{ total }}
          GFAL
        </p>
        <p class="text-secondary-200 text-right">
          ~
          {{ totalUSD }}
          USD
        </p>
      </div>
    </div>
    <i18n-t
      tag="p"
      class="text-xs text-secondary-200 text-right"
      keypath="main.modalOfferBegin.quoteUpdate"
      scope="global">
      <template #time>
        <span> {{ remainingSeconds }}</span>
      </template>
    </i18n-t>
    <div v-if="!kycUri" class="max-w-sm bg-primary-900 rounded-sm mt-8">
      <div class="flex items-center gap-2 py-2 pl-3 pr-4">
        <NuxtIcon
          name="mdi:warning"
          class="w-4 h-4 text-warning-300 shrink-0" />
        <p class="text-warning-300 text-xs">
          {{ $t(translationKey) }}
        </p>
      </div>
    </div>
    <div v-else class="mt-4">
      <CommonKYCNotice />
    </div>
    <div class="mt-5 mb-2 sm:mt-6 flex">
      <div v-if="!kycUri" class="w-full mt-auto flex items-center gap-4">
        <BaseButtonSubmit
          :loading="isSubmitting"
          :disabled="isLoading || errorMessage || isSubmitting"
          :text="$t('main.modalOfferBegin.confirm')"
          size="long"
          data-test="button-confirm-offer"
          :contextual-styles="true"
          @click="transfer" />
        <BaseButtonCancel
          :text="$t('main.modalOfferBegin.back')"
          :class="{ hidden: isSubmitting }"
          data-test="button-go-back-offer"
          :contextual-styles="true"
          @click="$emit('back')" />
      </div>
      <div v-else class="w-full mt-auto flex items-center gap-4">
        <div class="flex items-center gap-4">
          <a
            id="get_verified_button"
            data-test="button-get-verified-offer"
            target="_self"
            :href="kycUri"
            class="h-8 rounded-md bg-primary-500 px-6 py-4 font-bold text-sm text-neutral-200 shadow-xs border-2 border-primary-900 cursor-pointer flex items-center">
            {{ $t("main.modalOfferBegin.getVerified") }}
          </a>

          <BaseButtonCancel
            id="cancel_buy_button"
            type="button"
            data-test="button-cancel-get-verified-offer"
            :text="$t('main.modalOfferBegin.cancel')"
            :contextual-styles="true"
            @click="$emit('close')" />
        </div>
      </div>
    </div>
  </div>
</template>
